class GiftWithPurchase {
  constructor() {
    console.log('GWP: Initializing Gift With Purchase');
    this.container = document.querySelector('.gift-with-purchase');
    
    if (!this.container) {
      console.log('GWP: Container not found - feature may be disabled or product not selected');
      return;
    }

    console.log('GWP: Container found', this.container);

    this.threshold = parseInt(this.container.dataset.gwpThreshold, 10);
    this.variantId = parseInt(this.container.dataset.gwpVariantId, 10);
    this.mode = this.container.dataset.gwpMode;
    this.productHandle = this.container.dataset.gwpProductHandle;
    this.currency = this.container.dataset.gwpCurrency || '';
    this.storageKey = `gwp-declined:${this.productHandle || this.variantId}:${this.currency}`;
    this.popup = document.getElementById('gwp-popup');
    this.isProcessing = false;
    this.popupIsOpen = false;

    console.log('GWP: Config loaded', {
      threshold: this.threshold,
      variantId: this.variantId,
      mode: this.mode,
      productHandle: this.productHandle,
      popupElement: this.popup
    });

    this.init();
  }

  init() {
    this.checkThreshold();
    this.setupEventListeners();
    this.listenToCartUpdates();
  }

  setupEventListeners() {
    if (!this.popup) {
      console.log('GWP: No popup element, cannot setup listeners');
      return;
    }

    const closeButton = this.popup.querySelector('.gwp-popup-close');
    const overlay = this.popup.querySelector('.gwp-popup-overlay');
    const addButton = this.popup.querySelector('.gwp-popup-add');
    const declineButton = this.popup.querySelector('.gwp-popup-decline');

    if (closeButton) {
      closeButton.addEventListener('click', () => this.declineGift());
    }

    if (overlay) {
      overlay.addEventListener('click', () => this.declineGift());
    }

    if (addButton) {
      addButton.addEventListener('click', () => this.addGiftToCart());
    }

    if (declineButton) {
      declineButton.addEventListener('click', () => this.declineGift());
    }

    console.log('GWP: Event listeners attached', {
      closeButton: !!closeButton,
      overlay: !!overlay,
      addButton: !!addButton,
      declineButton: !!declineButton
    });
  }

  listenToCartUpdates() {
    document.addEventListener('cart:updated', () => {
      this.updateCartTotal();
    });

    if (typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.updateCartTotal();
      });
    }
  }

  async updateCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.checkThreshold(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  checkThreshold(cart = null) {
    if (this.isProcessing) return;

    if (!cart) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cartData => this.processThreshold(cartData))
        .catch(error => console.error('Error fetching cart:', error));
    } else {
      this.processThreshold(cart);
    }
  }

  processThreshold(cart) {
    // Find GWP items in cart
    const giftItems = cart.items.filter(item => 
      item.properties && item.properties._gift_with_purchase === 'true'
    );
    const giftInCart = giftItems.length > 0;
    
    // Calculate cart total EXCLUDING gift items
    let cartTotalExcludingGifts = cart.total_price;
    giftItems.forEach(giftItem => {
      cartTotalExcludingGifts -= (giftItem.final_line_price || 0);
    });
    
    const thresholdReached = cartTotalExcludingGifts >= this.threshold;

    console.log('GWP Debug:', {
      cartTotal: cart.total_price,
      cartTotalExcludingGifts,
      threshold: this.threshold,
      thresholdReached,
      giftInCart,
      giftItems: giftItems.length,
      popupIsOpen: this.popupIsOpen,
      mode: this.mode,
      variantId: this.variantId
    });

    this.updateProgressBar(cartTotalExcludingGifts, thresholdReached, giftInCart);

    if (!thresholdReached) {
      this.clearDeclinedGift();
    }

    // If threshold reached and gift NOT in cart, offer it
    if (thresholdReached && !giftInCart) {
      if (this.hasDeclinedGift()) {
        console.log('GWP: Gift previously declined this session, skipping offer');
        return;
      }

      if (this.mode === 'auto') {
        this.addGiftToCart();
      } else if (this.mode === 'popup') {
        console.log('GWP: Showing popup');
        this.showPopup();
      }
    }
    // If threshold NOT reached and gift IS in cart, remove it
    else if (!thresholdReached && giftInCart) {
      console.log('GWP: Cart below threshold, removing gift');
      this.removeGiftFromCart();
    }
    // If gift was added, close popup
    else if (giftInCart && this.popupIsOpen) {
      this.clearDeclinedGift();
      this.closePopup();
    }
  }

  hasDeclinedGift() {
    try {
      return sessionStorage.getItem(this.storageKey) === '1';
    } catch (error) {
      return false;
    }
  }

  markGiftDeclined() {
    try {
      sessionStorage.setItem(this.storageKey, '1');
    } catch (error) {
      // Ignore storage failures (private mode, etc.)
    }
  }

  clearDeclinedGift() {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      // Ignore storage failures
    }
  }

  updateProgressBar(cartTotal, thresholdReached, giftInCart) {
    const progressBar = this.container.querySelector('.gwp-progress-bar');
    const progressFill = this.container.querySelector('.gwp-progress-fill');
    const progressMessage = this.container.querySelector('.gwp-progress-message');
    const successMessage = this.container.querySelector('.gwp-success-message');
    const declined = this.hasDeclinedGift() && !giftInCart;

    if (declined) {
      this.hideOffer();
      return;
    }

    this.showOffer();

    if (!progressBar || !progressFill) return;

    const percentage = Math.min((cartTotal / this.threshold) * 100, 100);
    progressFill.style.width = `${percentage}%`;

    const showSuccess = thresholdReached && giftInCart;
    const showProgress = !thresholdReached && !giftInCart;

    if (showSuccess) {
      progressBar.classList.add('gwp-complete');
    } else {
      progressBar.classList.remove('gwp-complete');
    }

    if (progressMessage) {
      progressMessage.hidden = !showProgress;
      progressMessage.style.display = showProgress ? 'block' : 'none';
    }

    if (successMessage) {
      successMessage.hidden = !showSuccess;
      successMessage.style.display = showSuccess ? 'flex' : 'none';
    }
  }

  hideOffer() {
    if (!this.container) return;
    this.container.hidden = true;
    this.closePopup();
  }

  showOffer() {
    if (!this.container) return;
    this.container.hidden = false;
  }

  showPopup() {
    if (!this.popup) {
      console.log('GWP: Popup element not found, retrying...');
      // Popup element might have been replaced, try to find it again
      this.popup = document.getElementById('gwp-popup');
      if (!this.popup) return;
    }
    
    if (this.popupIsOpen && this.popup.style.display === 'block') return;
    
    this.popup.style.display = 'block';
    document.body.style.overflow = 'hidden';
    this.popupIsOpen = true;
    console.log('GWP: Popup opened');
  }

  closePopup() {
    if (!this.popup) return;
    
    this.popup.style.display = 'none';
    document.body.style.overflow = '';
    this.popupIsOpen = false;
    console.log('GWP: Popup closed');
  }

  async addGiftToCart() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    const addButton = this.popup?.querySelector('.gwp-popup-add');
    if (addButton) {
      addButton.classList.add('loading');
      addButton.disabled = true;
    }

    try {
      const miniCart = document.querySelector('mini-cart');
      
      const formData = new FormData();
      formData.append('items[0][id]', this.variantId);
      formData.append('items[0][quantity]', '1');
      formData.append('items[0][properties][_gift_with_purchase]', 'true');
      
      if (miniCart) {
        const sections = miniCart.getSectionsToRender().map((section) => section.id);
        sections.forEach(section => formData.append('sections[]', section));
      }
      formData.append('sections_url', window.location.pathname);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${theme.routes.cart_add_url}`, config);
      const parsedResponse = await response.json();

      if (parsedResponse.status) {
        console.error('GWP: Failed to add gift to cart', parsedResponse);
        // Avoid re-offering a gift that cannot be added (e.g. unavailable in market)
        this.markGiftDeclined();
        this.closePopup();
        return;
      }

      console.log('GWP: Gift added successfully', parsedResponse);
      this.closePopup();
      
      // Update cart UI using the miniCart's renderContents method
      if (miniCart) {
        miniCart.renderContents(parsedResponse);
      }
      
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-with-purchase' });
      }
      
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
    } catch (error) {
      console.error('Error adding gift to cart:', error);
      this.closePopup();
    } finally {
      this.isProcessing = false;
      if (addButton) {
        addButton.classList.remove('loading');
        addButton.disabled = false;
      }
    }
  }

  showLoadingOverlay(message = 'Updating cart...') {
    // Find the mini-cart container
    const miniCartContainer = document.getElementById('mini-cart');
    if (!miniCartContainer) return;
    
    // Create loading overlay if it doesn't exist
    let overlay = miniCartContainer.querySelector('.gwp-loading-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'gwp-loading-overlay';
      overlay.innerHTML = `
        <div class="gwp-loading-content">
          <div class="gwp-loading-spinner"></div>
          <p class="gwp-loading-message">${message}</p>
        </div>
      `;
      miniCartContainer.appendChild(overlay);
    }
    overlay.style.display = 'flex';
    
    // Disable checkout buttons in mini-cart
    const checkoutButtons = miniCartContainer.querySelectorAll('button[name="checkout"], .checkout-button, button[type="submit"][name="checkout"]');
    checkoutButtons.forEach(btn => {
      btn.disabled = true;
      btn.setAttribute('data-gwp-disabled', 'true');
    });
    
    console.log('GWP: Loading overlay shown over cart, checkout disabled');
  }

  hideLoadingOverlay() {
    const miniCartContainer = document.getElementById('mini-cart');
    if (miniCartContainer) {
      const overlay = miniCartContainer.querySelector('.gwp-loading-overlay');
      if (overlay) {
        overlay.style.display = 'none';
      }
    }
    
    // Re-enable checkout buttons
    const checkoutButtons = document.querySelectorAll('button[data-gwp-disabled="true"]');
    checkoutButtons.forEach(btn => {
      btn.disabled = false;
      btn.removeAttribute('data-gwp-disabled');
    });
    
    console.log('GWP: Loading overlay hidden, checkout enabled');
  }

  async removeGiftFromCart() {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    this.showLoadingOverlay('Removing gift from cart...');

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Find ALL gift items (in case there are duplicates)
      const giftItems = cart.items.filter(item => 
        item.properties && item.properties._gift_with_purchase === 'true'
      );
      
      if (giftItems.length > 0) {
        console.log(`GWP: Removing ${giftItems.length} gift item(s) from cart`);
        
        // Remove all gift items
        const updates = {};
        giftItems.forEach((item, index) => {
          updates[item.key] = 0;
        });
        
        const miniCart = document.querySelector('mini-cart');
        const formData = new FormData();
        
        Object.keys(updates).forEach(key => {
          formData.append(`updates[${key}]`, updates[key]);
        });
        
        if (miniCart) {
          const sections = miniCart.getSectionsToRender().map((section) => section.id);
          sections.forEach(section => formData.append('sections[]', section));
        }
        formData.append('sections_url', window.location.pathname);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const updateResponse = await fetch(`${theme.routes.cart_update_url}`, config);
        const parsedResponse = await updateResponse.json();

        if (parsedResponse && !parsedResponse.status) {
          console.log('GWP: Gift removed successfully');
          
          // Update cart UI
          if (miniCart) {
            miniCart.renderContents(parsedResponse);
          }
          
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-with-purchase' });
          }
          
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      }
    } catch (error) {
      console.error('Error removing gift from cart:', error);
    } finally {
      this.hideLoadingOverlay();
      this.isProcessing = false;
    }
  }

  declineGift() {
    this.markGiftDeclined();
    this.closePopup();
    this.hideOffer();
  }

  formatMoney(cents) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }
}

console.log('GWP: Script loaded, readyState:', document.readyState);

let gwpInstance = null;

function initGWP() {
  console.log('GWP: Attempting to initialize');
  
  // If instance already exists, refresh the popup reference and check threshold
  if (gwpInstance && gwpInstance.container) {
    console.log('GWP: Instance already exists, refreshing popup reference');
    const wasPopupOpen = gwpInstance.popupIsOpen;
    
    // Get new popup element (it was replaced during cart update)
    gwpInstance.popup = document.getElementById('gwp-popup');
    
    // Re-setup event listeners on the new popup element
    gwpInstance.setupEventListeners();
    
    gwpInstance.checkThreshold();
    
    // If popup was open before cart update, reopen it
    if (wasPopupOpen && gwpInstance.popup) {
      gwpInstance.popup.style.display = 'block';
      document.body.style.overflow = 'hidden';
      console.log('GWP: Popup re-opened after cart update');
    }
    return;
  }
  
  gwpInstance = new GiftWithPurchase();
}

// Initialize on page load
if (document.readyState === 'loading') {
  console.log('GWP: Waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', initGWP);
} else {
  console.log('GWP: DOM already loaded, initializing immediately');
  initGWP();
}

// Re-initialize when cart updates (for dynamic mini-cart)
document.addEventListener('cart:updated', () => {
  console.log('GWP: Cart updated event detected, re-initializing');
  setTimeout(initGWP, 100);
});

// Listen for cart drawer opening (if theme uses this event)
if (typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    console.log('GWP: PUB_SUB cart update detected, re-initializing');
    setTimeout(initGWP, 100);
  });
}

// Watch for gift-with-purchase element to be added to DOM
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.addedNodes.length) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          // Check if the node itself or any child has the gift-with-purchase class
          if (node.classList && node.classList.contains('gift-with-purchase')) {
            console.log('GWP: Gift-with-purchase element detected in DOM, initializing');
            setTimeout(initGWP, 100);
            return;
          } else if (node.querySelector && node.querySelector('.gift-with-purchase')) {
            console.log('GWP: Gift-with-purchase element found in added node, initializing');
            setTimeout(initGWP, 100);
            return;
          }
        }
      }
    }
  }
});

// Start observing
observer.observe(document.body, {
  childList: true,
  subtree: true
});

console.log('GWP: Mutation observer started - watching for .gift-with-purchase');

